import React from 'react';
import styled from '@emotion/styled';
import {
  palette,
  radius,
  shadow,
  spacing,
  transition,
  typography,
  zIndex,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { useDialogFocus } from '../hooks/use-dialog-focus';
import { ModalPortal } from '../modal/modal-portal';
import { IqHandoffCard } from '../iq/iq-handoff-card';
import { Handoff } from '../../interfaces/handoff-interfaces';
// THE PRODUCT'S BIG PRIMARY PRESS, IMPORTED RATHER THAN RE-DRAWN. The takeover's
// dismiss is the same weight of control as Clock in — it is the one thing to do
// on that screen — so it has to be the same button, not a look-alike. A second
// copy of the 72px recipe is how two "primary" buttons drift apart.
import { PrimaryAction } from './shifts.styled';
import {
  Presence,
  m,
  overlayMotion,
  scrimMotion,
  useOverlayMotion,
} from '../../motion';

/**
 * THE HANDOVER, AS A LAYER OVER THE SCREEN — not as a panel inside a card.
 *
 * ═══════════════ WHY IT MOVED ═══════════════
 * The handover used to render INSIDE <ShiftClock>, which sits in the main
 * menu's narrow title column. A shift summary is not a card's worth of content:
 * it carries the window, the outgoing person, a written summary, the log facts
 * behind it, and a provenance line. In a ~420px column that stacked into an
 * unreadable ribbon and overflowed its own container — the one piece of writing
 * in the product that somebody has to READ, in the narrowest box on the screen.
 *
 * A modal is the honest shape for it. It is a document, it is read once, and
 * reading it is the only thing happening while it is open.
 *
 * ═══════════════ IT IS PORTALED, AND THAT IS NOT OPTIONAL ═══════════════
 * See ../modal/modal-portal.tsx: the menu's own entrance animation creates a
 * transform containing block, and a `position: fixed` scrim rendered inside it
 * covers the pane rather than the viewport.
 *
 * ═══════════════ THE AURORA STAYS ON THE CARD ═══════════════
 * <IqHandoffCard> carries the aurora because the CONTENT is model-written. This
 * wrapper does not: a scrim and a close button are chrome, and design_system.md
 * reserves the gradient for Lobbero IQ itself.
 *
 * ═══════════════ MOTION ═══════════════
 * A dialog grows from the centre ('modal'); the takeover RISES ('takeover'),
 * because a full-bleed surface scaling from the centre reads as a zoom, and the
 * same surface rising a little settles — the difference between "a box
 * appeared" and "this is now your screen". Owner instruction, 2026-08-21: "he
 * should receive the handover in his iq on fullscreen immediately in an
 * animated fashion smoothly." Both leave the way they came. Reduced motion is
 * honoured app-wide by MotionRoot (transforms dropped, opacity kept), so there
 * is no local override here any more.
 */

const Scrim = m.div<{ $takeover?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  display: flex;
  align-items: ${({ $takeover }) => ($takeover ? 'stretch' : 'center')};
  justify-content: center;
  padding: ${({ $takeover }) => ($takeover ? '0' : spacing.md)};
  background: ${palette.overlay};

  /* Full-height sheet on a phone. A shift summary is long, and a centred card
     with margins on four sides wastes the axis it needs most. */
  ${down('sm')} {
    align-items: stretch;
    padding: 0;
  }
`;

const Card = m.div<{ $takeover?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${({ $takeover }) => ($takeover ? 'none' : '720px')};
  /* The card never grows past the viewport; the BODY scrolls, so the title and
     the close button stay reachable however long the summary is. The dvh pair
     is the iOS rule from the responsive pass: vh alone is the tall-toolbar
     value there, so the last lines sit under the browser chrome. */
  max-height: calc(100vh - ${spacing.xl});
  max-height: calc(100dvh - ${spacing.xl});
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};

  ${({ $takeover }) => ($takeover
    ? `
    max-height: 100vh;
    max-height: 100dvh;
    height: 100vh;
    height: 100dvh;
    border: none;
    border-radius: 0;
  `
    : '')}

  ${down('sm')} {
    max-width: none;
    max-height: 100vh;
    max-height: 100dvh;
    border: none;
    border-radius: 0;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding: ${spacing.md} ${spacing.md} ${spacing.sm};
  border-bottom: 1px solid ${palette.line};
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: -0.014em;
  color: ${palette.ink};
`;

const Close = styled.button`
  -webkit-appearance: none;
  appearance: none;
  flex: none;
  min-height: 44px;
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.pill};
  background: transparent;
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    border-color: ${palette.lineStrong};
    color: ${palette.inkSoft};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: ${spacing.md};
`;

/* ── THE TAKEOVER'S ONE PRESS, AT THE BOTTOM WHERE IT IS LOOKED FOR ──
   Owner instruction, 2026-09-04: "the start my shift button should be a lot
   bigger like clock in and centered on the bottom so i can see it."

   It was a 44px outline pill in the top-right corner of the header — the same
   chrome an ordinary dialog uses for "Done". That is right for a dialog somebody
   OPENED and is finished with, and wrong here: the takeover is raised WITHOUT
   being asked for, it fills the screen, and the reader's job is to accept a
   shift. On a long handover the header scrolls out of reach of the eye (the Body
   scrolls, not the Head, so the pill stays on screen — but at the far corner,
   above a wall of text, which is not where anybody looks for "what do I do now").

   So the action moves to a bar of its own, pinned below the document, carrying
   the same 72px press as the clock-in gate. `flex: none` beside the Body's
   `flex: 1 1 auto` is what keeps it out of the scroll: the document scrolls, the
   bar does not.

   CENTRED AND CAPPED. The takeover is full-bleed, so a full-width button on a
   1920px monitor would be a 1900px slab. The cap is the reading width the card
   already uses. */
const Foot = styled.div`
  flex: none;
  display: flex;
  justify-content: center;
  padding: ${spacing.sm} ${spacing.md};
  /* The safe area, so the press is not under a home indicator. */
  padding-bottom: calc(${spacing.sm} + var(--safe-bottom));
  border-top: 1px solid ${palette.line};
  background: ${palette.surface};
`;

const FootInner = styled.div`
  width: 100%;
  max-width: 720px;
`;

export interface HandoffModalProps {
  open: boolean;
  handoff: Handoff | null;
  /** Passed straight through to the card — it decides the copy from this. */
  viewer: 'outgoing' | 'recipient' | 'observer';
  buildingName?: string;
  title?: string;
  /** FULL-BLEED, and a slower entrance. For the one moment this layer is raised
   *  WITHOUT the reader asking for it: they clocked in, the desk's pending
   *  handover was claimed in that same transaction, and the shift they are
   *  taking over is now the whole screen.
   *
   *  A variant rather than a second component, deliberately — the focus trap,
   *  the Escape handling, the portal and the scroll behaviour are the parts that
   *  are easy to get subtly wrong, and a copy of them would be a copy of four
   *  correct decisions waiting to drift. */
  takeover?: boolean;
  /** The dismiss control's label. A takeover is not "Done" — nothing was
   *  completed; the reader is acknowledging what they just took on. */
  dismissLabel?: string;
  onClose: () => void;
}

export const HandoffModal: React.FC<HandoffModalProps> = ({
  open,
  handoff,
  viewer,
  buildingName,
  title = 'Shift handover',
  takeover = false,
  dismissLabel,
  onClose,
}) => {
  // Escape and the focus trap come from the shared stack, so this layer plays
  // by the same rules as every other dialog when one is opened on top of it.
  const dialogRef = useDialogFocus(open, onClose);
  const modalMotion = useOverlayMotion('modal');
  const cardMotion = takeover ? overlayMotion('takeover') : modalMotion;

  return (
    <Presence>
      {open && handoff ? (
        <ModalPortal key="handoff-modal">
          {/* The scrim dismisses; the card swallows the click so a press inside
              the summary does not close the thing being read. */}
          {/* A TAKEOVER DOES NOT DISMISS ON THE SCRIM. There is no scrim to hit —
              it is full-bleed — and more to the point this is a record somebody
              is being handed responsibility for: a stray tap must not be able
              to discard it. Escape and the button remain. */}
          <Scrim
            {...scrimMotion}
            $takeover={takeover}
            onClick={takeover ? undefined : onClose}
          >
            <Card
              {...cardMotion}
              $takeover={takeover}
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-label={title}
              onClick={event => event.stopPropagation()}
            >
              <Head>
                <Title>{title}</Title>
                {/* ONE DISMISS, NOT TWO. On a takeover the press lives in the
                    foot bar below, so a second control up here would be two
                    buttons for one act — and the smaller, quieter one sits where
                    the eye lands first. Escape still closes either. */}
                {takeover ? null : (
                  <Close type="button" onClick={onClose}>
                    {dismissLabel || 'Done'}
                  </Close>
                )}
              </Head>
              <Body>
                <IqHandoffCard
                  handoff={handoff}
                  viewer={viewer}
                  buildingName={buildingName}
                  /* This modal's Card already draws the frame — see IqCard's
                     $flush note on the doubled border. */
                  flush
                />
              </Body>
              {takeover ? (
                <Foot>
                  <FootInner>
                    <PrimaryAction $big type="button" onClick={onClose}>
                      {dismissLabel || 'Done'}
                    </PrimaryAction>
                  </FootInner>
                </Foot>
              ) : null}
            </Card>
          </Scrim>
        </ModalPortal>
      ) : null}
    </Presence>
  );
};
